// Minimal, dependency-free Markdown → safe HTML, used for the FKE genAnswer
// narrative (OKV-234). The fke-widget renders the answer with `marked` + DOMPurify;
// the OKV frontend ships neither and builds on Node 14, so rather than add those
// dependencies we render the small subset the AI answers actually use.
//
// Safety: the source is backend-generated (semi-trusted) text injected via v-html,
// so every character is HTML-escaped FIRST and only a fixed set of tags is then
// re-introduced from controlled patterns (bold/italic/code/links/lists/headings).
// There is no path for raw markup from the input to reach the DOM, so no separate
// sanitizer is needed and it stays compatible with a strict CSP (no eval).

function escapeHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// Inline spans, applied to already-escaped text. Order matters: links and bold
// before italic so a `**` run is consumed before a single `*` can match it.
function renderInline(text) {
  let out = text
  // [label](http(s)/mailto url) — href restricted to safe protocols.
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/g,
    (m, label, href) => `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`
  )
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>')
  out = out.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>')
  out = out.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, '$1<em>$2</em>')
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>')
  return out
}

export function renderMarkdown(md) {
  if (!md) return ''
  const lines = escapeHtml(String(md).replace(/\r\n/g, '\n')).split('\n')
  const html = []
  let paragraph = []
  let i = 0

  const flushParagraph = () => {
    if (paragraph.length) {
      html.push('<p>' + renderInline(paragraph.join('<br>')) + '</p>')
      paragraph = []
    }
  }

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()

    if (trimmed === '') {
      flushParagraph()
      i++
      continue
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed)
    if (heading) {
      flushParagraph()
      html.push('<p class="md-heading">' + renderInline(heading[2]) + '</p>')
      i++
      continue
    }

    if (/^[-*]\s+/.test(trimmed)) {
      flushParagraph()
      const items = []
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push('<li>' + renderInline(lines[i].trim().replace(/^[-*]\s+/, '')) + '</li>')
        i++
      }
      html.push('<ul>' + items.join('') + '</ul>')
      continue
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      flushParagraph()
      const items = []
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push('<li>' + renderInline(lines[i].trim().replace(/^\d+\.\s+/, '')) + '</li>')
        i++
      }
      html.push('<ol>' + items.join('') + '</ol>')
      continue
    }

    paragraph.push(trimmed)
    i++
  }
  flushParagraph()
  return html.join('')
}
